import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { usePaywall } from "../components/PaywallModal";
import { handlePaywallResponse } from "../utils/paywallHelper";
import {
  Mic,
  Send,
  ArrowLeft,
  ChevronRight,
  Loader2,
  MessageSquare,
  Star,
  Target,
  TrendingUp,
  Award,
  Clock,
  CheckCircle,
  RefreshCw,
  Plus,
  History,
  Briefcase,
  Video,
  VideoOff,
  Circle,
  Square,
  Download
} from "lucide-react";

const InterviewPrep = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showPaywall } = usePaywall();

  const [phase, setPhase] = useState("setup"); // setup | generating | interview | feedback
  const [session, setSession] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);

  // Video recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState({});
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const [setupForm, setSetupForm] = useState({
    job_title: "",
    company_name: "",
    focus_areas: "",
    question_count: 5,
  });

  useEffect(() => {
    fetchHistory();
    return () => stopCamera();
  }, []);

  // ── Video Recording Functions ──
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 320, height: 240, facingMode: "user" },
        audio: true,
      });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
      }
      setCameraEnabled(true);
    } catch (err) {
      setCameraError("Camera access denied. Enable camera to record your practice.");
      setCameraEnabled(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = null;
    }
    setCameraEnabled(false);
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(() => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setRecordedChunks((prev) => ({ ...prev, [currentQ]: { blob, url } }));
    };
    mediaRecorderRef.current = recorder;
    recorder.start(1000);
    setIsRecording(true);
  }, [currentQ]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const downloadRecording = (qIndex) => {
    const rec = recordedChunks[qIndex];
    if (!rec) return;
    const a = document.createElement("a");
    a.href = rec.url;
    a.download = `interview_q${qIndex + 1}_${session?.job_title || "practice"}.webm`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/interview-prep/history`, { credentials: "include", headers });
      if (res.ok) setHistory(await res.json());
    } catch {}
  };

  const startSession = async () => {
    if (!setupForm.job_title.trim()) return toast.error("Enter a job title");
    setPhase("generating");

    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/interview-prep/start`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          job_title: setupForm.job_title,
          company_name: setupForm.company_name || null,
          focus_areas: setupForm.focus_areas ? setupForm.focus_areas.split(",").map((s) => s.trim()).filter(Boolean) : null,
          question_count: setupForm.question_count,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setCurrentQ(0);
        setFeedbackMap({});
        setPhase("interview");
        toast.success("Interview session started!");
      } else {
        if (await handlePaywallResponse(res, showPaywall)) { setPhase("setup"); return; }
        const err = await res.json();
        toast.error(err.detail || "Failed to start session");
        setPhase("setup");
      }
    } catch {
      toast.error("Failed to start. Try again.");
      setPhase("setup");
    }
  };

  const submitAnswer = async () => {
    if (!answer.trim()) return toast.error("Please type your answer");
    setSubmitting(true);

    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API}/interview-prep/answer`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({
          session_id: session.session_id,
          question_index: currentQ,
          answer: answer,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setFeedbackMap((prev) => ({ ...prev, [currentQ]: data.feedback }));
        setPhase("feedback");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to get feedback");
      }
    } catch {
      toast.error("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const nextQuestion = () => {
    if (currentQ < (session?.questions?.length || 0) - 1) {
      setCurrentQ(currentQ + 1);
      setAnswer("");
      setPhase("interview");
    } else {
      setPhase("summary");
    }
  };

  const loadSession = async (sessionId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/interview-prep/session/${sessionId}`, { credentials: "include", headers });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        const fbMap = {};
        (data.feedback || []).forEach((f) => { fbMap[f.question_index] = f; });
        setFeedbackMap(fbMap);
        setPhase("summary");
        setShowHistory(false);
      }
    } catch {
      toast.error("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-obsidian border border-[#333] rounded px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-crimson focus:outline-none";
  const questions = session?.questions || [];
  const allFeedback = Object.values(feedbackMap);
  const avgScore = allFeedback.length > 0 ? Math.round(allFeedback.reduce((s, f) => s + (f.score || 0), 0) / allFeedback.length * 10) : 0;

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <Sidebar />
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3" data-testid="interview-prep-title">
                <Mic className="w-7 h-7 text-crimson" />
                AI Interview Prep
              </h1>
              <p className="text-gray-400 mt-1 text-sm">Practice with AI-generated questions and get instant feedback</p>
            </div>
            <Button onClick={() => setShowHistory(!showHistory)} variant="outline" className="border-[#333] text-gray-300 hover:bg-white/5" data-testid="toggle-history">
              <History className="w-4 h-4 mr-2" /> History
            </Button>
          </div>

          {/* History Panel */}
          {showHistory && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-obsidian-light border border-[#222] rounded-lg p-5">
              <h3 className="font-semibold mb-3">Past Sessions</h3>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500">No sessions yet</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {history.map((s) => (
                    <button
                      key={s.session_id}
                      onClick={() => loadSession(s.session_id)}
                      className="w-full flex items-center justify-between p-3 bg-obsidian border border-[#333] rounded hover:border-crimson/50 transition-colors text-left"
                      data-testid={`history-${s.session_id}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{s.job_title}</p>
                        <p className="text-xs text-gray-500">{s.company_name || "General"} · {s.questions?.length || 0} questions · {s.status}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* SETUP PHASE */}
          {phase === "setup" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-obsidian-light border border-[#222] rounded-lg p-6 sm:p-8 space-y-5">
              <h2 className="text-xl font-bold">Start Mock Interview</h2>
              <p className="text-sm text-gray-400">AI will generate role-specific interview questions and provide feedback on your answers.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Job Title *</label>
                  <input type="text" value={setupForm.job_title} onChange={(e) => setSetupForm({ ...setupForm, job_title: e.target.value })} placeholder="e.g., Senior Software Engineer" className={inputClass} data-testid="prep-job-title" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Company (optional)</label>
                  <input type="text" value={setupForm.company_name} onChange={(e) => setSetupForm({ ...setupForm, company_name: e.target.value })} placeholder="e.g., Google" className={inputClass} data-testid="prep-company" />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Number of Questions</label>
                  <select value={setupForm.question_count} onChange={(e) => setSetupForm({ ...setupForm, question_count: parseInt(e.target.value) })} className={inputClass} data-testid="prep-count">
                    {[3, 5, 7, 10].map((n) => (<option key={n} value={n}>{n} questions</option>))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1">Focus Areas (comma-separated, optional)</label>
                  <input type="text" value={setupForm.focus_areas} onChange={(e) => setSetupForm({ ...setupForm, focus_areas: e.target.value })} placeholder="e.g., system design, leadership, coding" className={inputClass} data-testid="prep-focus" />
                </div>
              </div>

              <Button onClick={startSession} className="w-full bg-crimson hover:bg-crimson/90 text-white py-3" data-testid="start-interview-btn">
                <Mic className="w-4 h-4 mr-2" /> Start Mock Interview
              </Button>
            </motion.div>
          )}

          {/* GENERATING PHASE */}
          {phase === "generating" && (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-crimson mb-4" />
              <p className="text-gray-400">AI is preparing your interview questions...</p>
            </div>
          )}

          {/* INTERVIEW PHASE */}
          {phase === "interview" && session && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              {/* Progress */}
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span>Question {currentQ + 1} of {questions.length}</span>
                <div className="flex-1 h-1.5 bg-[#222] rounded-full overflow-hidden">
                  <div className="h-full bg-crimson transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
                </div>
              </div>

              <div className="bg-obsidian-light border border-[#222] rounded-lg p-6 sm:p-8 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-crimson/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageSquare className="w-4 h-4 text-crimson" />
                  </div>
                  <div>
                    <p className="text-lg font-semibold" data-testid="interview-question">{questions[currentQ]?.question}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-white/5 border border-[#333] rounded">{questions[currentQ]?.type}</span>
                      <span className="px-2 py-0.5 bg-white/5 border border-[#333] rounded">{questions[currentQ]?.difficulty}</span>
                    </div>
                  </div>
                </div>

                {/* Video Recording Panel */}
                <div className="border border-[#222] bg-obsidian rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Video className="w-4 h-4 text-crimson" />
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Video Practice</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!cameraEnabled ? (
                        <button onClick={startCamera} className="flex items-center gap-1.5 px-3 py-1.5 bg-crimson/10 border border-crimson/30 text-crimson text-xs hover:bg-crimson/20 transition-colors" data-testid="enable-camera-btn">
                          <Video className="w-3 h-3" /> Enable Camera
                        </button>
                      ) : (
                        <>
                          {!isRecording ? (
                            <button onClick={startRecording} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 border border-red-500/30 text-red-400 text-xs hover:bg-red-500/30 transition-colors" data-testid="start-recording-btn">
                              <Circle className="w-3 h-3 fill-current" /> Record
                            </button>
                          ) : (
                            <button onClick={stopRecording} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-xs animate-pulse" data-testid="stop-recording-btn">
                              <Square className="w-3 h-3 fill-current" /> Stop
                            </button>
                          )}
                          <button onClick={stopCamera} className="p-1.5 text-gray-500 hover:text-white transition-colors" title="Disable camera">
                            <VideoOff className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {cameraError && <p className="text-xs text-amber-400 mb-2">{cameraError}</p>}
                  <div className="flex gap-3">
                    {/* Camera preview */}
                    <div className={`relative overflow-hidden bg-black rounded ${cameraEnabled ? "w-40 h-30" : "w-40 h-[120px]"} flex-shrink-0`}>
                      {cameraEnabled ? (
                        <>
                          <video ref={videoPreviewRef} autoPlay muted playsInline className="w-full h-full object-cover" />
                          {isRecording && (
                            <div className="absolute top-2 right-2 flex items-center gap-1">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                              <span className="text-[9px] text-red-400">REC</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                          <VideoOff className="w-6 h-6" />
                        </div>
                      )}
                    </div>
                    {/* Recorded playback for this question */}
                    {recordedChunks[currentQ] && (
                      <div className="flex-1">
                        <p className="text-[10px] text-gray-500 mb-1">Your recording for Q{currentQ + 1}:</p>
                        <video src={recordedChunks[currentQ].url} controls className="w-full max-h-[120px] rounded bg-black" />
                        <button onClick={() => downloadRecording(currentQ)} className="flex items-center gap-1 mt-1 text-[10px] text-crimson hover:underline" data-testid="download-recording-btn">
                          <Download className="w-3 h-3" /> Download
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Type your answer here... Be specific, use examples from your experience."
                  rows={6}
                  className={`${inputClass} resize-none`}
                  data-testid="answer-input"
                />

                <div className="flex justify-between items-center">
                  <p className="text-xs text-gray-500">Tip: Use the STAR method (Situation, Task, Action, Result)</p>
                  <Button onClick={submitAnswer} disabled={submitting || !answer.trim()} className="bg-crimson hover:bg-crimson/90 text-white" data-testid="submit-answer-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Submit Answer
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* FEEDBACK PHASE */}
          {phase === "feedback" && feedbackMap[currentQ] && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-lg">AI Feedback</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-crimson">{feedbackMap[currentQ].score}</span>
                    <span className="text-sm text-gray-500">/ {feedbackMap[currentQ].max_score || 10}</span>
                  </div>
                </div>

                {/* Score bar */}
                <div className="h-2 bg-[#222] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${feedbackMap[currentQ].score >= 7 ? "bg-emerald-500" : feedbackMap[currentQ].score >= 5 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${(feedbackMap[currentQ].score / (feedbackMap[currentQ].max_score || 10)) * 100}%` }} />
                </div>

                {/* Strengths */}
                {feedbackMap[currentQ].strengths?.length > 0 && (
                  <div>
                    <p className="text-xs text-emerald-400 font-medium mb-1 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Strengths</p>
                    <ul className="space-y-1">
                      {feedbackMap[currentQ].strengths.map((s, i) => (
                        <li key={i} className="text-sm text-gray-300 pl-4 border-l border-emerald-500/30">{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Improvements */}
                {feedbackMap[currentQ].improvements?.length > 0 && (
                  <div>
                    <p className="text-xs text-amber-400 font-medium mb-1 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Areas to Improve</p>
                    <ul className="space-y-1">
                      {feedbackMap[currentQ].improvements.map((s, i) => (
                        <li key={i} className="text-sm text-gray-300 pl-4 border-l border-amber-500/30">{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sample answer */}
                {feedbackMap[currentQ].sample_answer && (
                  <div className="bg-obsidian border border-[#333] rounded p-4">
                    <p className="text-xs text-gray-500 mb-1">Example Strong Answer</p>
                    <p className="text-sm text-gray-300">{feedbackMap[currentQ].sample_answer}</p>
                  </div>
                )}

                {/* Tip */}
                {feedbackMap[currentQ].tip && (
                  <div className="bg-crimson/5 border border-crimson/20 rounded p-3">
                    <p className="text-sm text-crimson"><Star className="w-4 h-4 inline mr-1" />{feedbackMap[currentQ].tip}</p>
                  </div>
                )}
              </div>

              <Button onClick={nextQuestion} className="w-full bg-crimson hover:bg-crimson/90 text-white" data-testid="next-question-btn">
                {currentQ < questions.length - 1 ? (
                  <><ChevronRight className="w-4 h-4 mr-2" /> Next Question</>
                ) : (
                  <><Award className="w-4 h-4 mr-2" /> View Summary</>
                )}
              </Button>
            </motion.div>
          )}

          {/* SUMMARY PHASE */}
          {phase === "summary" && session && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="bg-obsidian-light border border-[#222] rounded-lg p-6 text-center space-y-3">
                <Award className="w-12 h-12 text-crimson mx-auto" />
                <h2 className="text-xl font-bold">Interview Complete!</h2>
                <p className="text-gray-400 text-sm">{session.job_title}{session.company_name ? ` at ${session.company_name}` : ""}</p>
                {allFeedback.length > 0 && (
                  <div className="inline-block bg-obsidian border border-[#333] rounded px-6 py-3 mt-2">
                    <p className="text-3xl font-bold text-crimson">{avgScore}%</p>
                    <p className="text-xs text-gray-500">Overall Score</p>
                  </div>
                )}
              </div>

              {/* Per-question breakdown */}
              <div className="space-y-3">
                {questions.map((q, qi) => {
                  const fb = feedbackMap[qi];
                  const rec = recordedChunks[qi];
                  return (
                    <div key={qi} className="bg-obsidian-light border border-[#222] rounded-lg p-4" data-testid={`summary-q-${qi}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-medium">Q{qi + 1}: {q.question}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                            <span className="px-1.5 py-0.5 bg-white/5 rounded">{q.type}</span>
                            <span className="px-1.5 py-0.5 bg-white/5 rounded">{q.difficulty}</span>
                          </div>
                        </div>
                        {fb ? (
                          <span className={`text-lg font-bold ${fb.score >= 7 ? "text-emerald-400" : fb.score >= 5 ? "text-amber-400" : "text-rose-400"}`}>
                            {fb.score}/{fb.max_score || 10}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600">Not answered</span>
                        )}
                      </div>
                      {rec && (
                        <div className="mt-3 border-t border-[#222] pt-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Video className="w-3 h-3 text-crimson" />
                            <span className="text-[10px] text-gray-500">Your video response</span>
                          </div>
                          <video src={rec.url} controls className="w-full max-h-[160px] rounded bg-black" />
                          <button onClick={() => downloadRecording(qi)} className="flex items-center gap-1 mt-1 text-[10px] text-crimson hover:underline">
                            <Download className="w-3 h-3" /> Download recording
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <Button onClick={() => { stopCamera(); setPhase("setup"); setSession(null); setFeedbackMap({}); setAnswer(""); setCurrentQ(0); setRecordedChunks({}); }} variant="outline" className="flex-1 border-[#333] text-gray-300 hover:bg-white/5" data-testid="new-session-btn">
                  <Plus className="w-4 h-4 mr-2" /> New Session
                </Button>
                <Button onClick={() => navigate("/assessments")} className="flex-1 bg-crimson hover:bg-crimson/90 text-white">
                  <Target className="w-4 h-4 mr-2" /> Take Assessments
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default InterviewPrep;
